import { useCallback, useMemo, useState } from "react";
import KaboodleModal from "./KaboodleModal";
import { KaboodleToaster } from "components/common";
import Card from "components/Card/Card";
import type { OptionType } from "components/inputs";
import { Select } from "components/inputs";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "utils/redux";
import Button from "components/Button/Button";
import { useUploadClaimsImportForRREMutation } from "services/rre-api";
import { useDropzone } from "react-dropzone";
import "./UploadClaimAndQueryModal.scss";
import { useConstants } from "utils/hooks";

type UploadClaimAndQueryModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

type FormData = {
  importType: OptionType;
};

const UploadClaimAndQueryModal: React.FC<UploadClaimAndQueryModalProps> = ({
  onClose,
  isOpen,
}: UploadClaimAndQueryModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadRRE, { isLoading }] = useUploadClaimsImportForRREMutation();
  const currentRRE = useSelector((state) => state.rre.currentRRE);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setUploadError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const {
    control,
    reset,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      importType: { label: "Claim", value: "C" },
    },
  });

  const { constants } = useConstants();
  // only include claim and query import types
  const importTypes = (constants?.IMPORT_TYPES || []).filter(
    (importType) =>
      !!importType.value && ["C", "Q"].includes(importType.value.toString()),
  );

  const footerContent = useMemo(() => {
    const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
      const callData = new FormData();
      callData.append("file", file);
      callData.append("import_type", data.importType.value.toString());
      await uploadRRE({
        id: currentRRE?.id,
        file: callData,
      })
        .unwrap()
        .then(() => {
          reset();
          onClose();
          KaboodleToaster.success(
            "Successfully uploaded. Once complete the result will show in the table.",
          );
        })
        .catch((error) => {
          setUploadError(error?.data?.error || "An error occurred");
          KaboodleToaster.error("An error occurred while uploading.");
        });
    };

    return (
      <div>
        <div className="upload-button">
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={!file || isLoading}
          >
            {uploadError ? "Try Again" : isLoading ? "Uploading..." : "Upload"}
          </Button>
        </div>
        {uploadError && (
          <div className="error-footer">
            <p>{uploadError}</p>
            <Button onClick={() => setUploadError(null)}>Clear</Button>
          </div>
        )}
      </div>
    );
  }, [
    currentRRE?.id,
    file,
    handleSubmit,
    isLoading,
    onClose,
    reset,
    uploadError,
    uploadRRE,
  ]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Upload Data">
        <Controller
          control={control}
          name="importType"
          rules={{ required: "Import type is required" }}
          render={({ field: { name, onChange } }) => (
            <Select
              name={name}
              placeholder="- Select Import Type -"
              label="Import Type"
              options={importTypes}
              onChange={(option) => {
                onChange(option);
                clearErrors();
              }}
              errors={errors}
            />
          )}
        />
        <div className="claim-upload-file">
          <label htmlFor="name">File</label>
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? "active" : "active"}`}
          >
            <input {...getInputProps()} />
            {file ? (
              <p>
                <b>Selected file:</b> {file.name}
              </p>
            ) : (
              <p>
                <b>Drag & Drop</b> or <b>Click</b> to Select File
              </p>
            )}
          </div>
        </div>
      </Card>
    </KaboodleModal>
  );
};

export default UploadClaimAndQueryModal;
